import type {
  AccountStatement,
  AccountStatementFilters,
  PaginatedAccountStatements,
  SignedUrlResponse,
} from '@/types/accountStatements';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Account Statements Service
 * Handles API calls to /api/v1/account-statements/* endpoints
 */
export const accountStatementsService = {
  /**
   * Get all account statements with filters (paginated)
   */
  async getStatements(
    filters: AccountStatementFilters = {}
  ): Promise<PaginatedAccountStatements> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const url = `/api/v1/account-statements${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedAccountStatements>(url, { method: 'GET' }, token);
  },

  /**
   * Get a single account statement by ID
   */
  async getStatement(id: string): Promise<AccountStatement> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<AccountStatement>(
      `/api/v1/account-statements/${id}`,
      { method: 'GET' },
      token
    );
  },

  /**
   * Get signed URL for PDF download
   */
  async getSignedUrl(id: string): Promise<SignedUrlResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<SignedUrlResponse>(
      `/api/v1/account-statements/${id}/signed-url`,
      { method: 'GET' },
      token
    );
  },
};
